import { useState } from 'react';
import { AxiosResponse } from 'axios';

import { useLoadingFile } from './useLoadingFile';
import { useUploadFileMutation } from './useMutation';

import { ASSET_CATEGORY, ASSET_TYPE } from '~types';
import { cloneWithNewFileName, sanitizeFileName } from '~utils/file.util';
import { toastError } from '~utils/toast.util';

export interface IUploadResponse {
  name: string;
  url: string;
  type: ASSET_TYPE;
}

export interface IPreviewFile extends Partial<File> {
  url?: string;
  fileName?: string;
}

export interface IUseUploadFileProps {
  handleUploadPhoto?: (file: IPreviewFile) => void;
  handleRemovePhoto?: () => void;
  initialImage?: IPreviewFile | null;
}

export const useUploadFile = ({
  handleUploadPhoto,
  handleRemovePhoto,
  initialImage = null,
}: IUseUploadFileProps) => {
  const [file, setFile] = useState<IPreviewFile | null>(initialImage);
  const { loadingState, startLoading, finishLoading, endLoading } =
    useLoadingFile();

  const uploadMutate = useUploadFileMutation();

  const onUploadFile = async (targetFile: File) => {
    const sanitizeFile = cloneWithNewFileName(
      targetFile,
      sanitizeFileName(targetFile.name),
    );

    startLoading();

    uploadMutate.mutate(
      {
        file: sanitizeFile,
        type: ASSET_TYPE.IMAGE,
        category: ASSET_CATEGORY.STATION,
      },
      {
        onSuccess: (data: AxiosResponse<IUploadResponse>) => {
          const newFile = Object.assign(targetFile, {
            fileName: data?.data?.name || '',
            url: data?.data?.url,
          });
          setFile(newFile);

          finishLoading();

          setTimeout(() => {
            endLoading();
          }, 1000);

          handleUploadPhoto?.(newFile);
        },

        onError: (error) => {
          toastError({
            error,
            axiosMessage: 'Something went wrong when upload file',
          });
          endLoading();
        },
      },
    );
  };

  const onRemoveFile = () => {
    setFile(null);
    handleRemovePhoto?.();
  };

  return { onRemoveFile, onUploadFile, file, loadingState };
};
